import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import {
  SvgProject,
  SvgCanvasRecord,
  SvgAsset,
  SvgCanvasHistory,
  SvgAiHistory,
  AiGenerateSvgRequest,
  AiGenerateSvgResponse,
  SvgCanvasData,
} from '../types/svgTypes';

// 新增：Dify /chat-messages 响应结构（只关心我们需要的字段）
interface DifyChatResponse {
  answer?: string;
  conversation_id?: string;
}

class SvgAPIService {
  private _instance: AxiosInstance;
  private _userId: string = 'default_user';

  // 新增：Dify ChatFlow 的 axios 实例
  private _difyInstance: AxiosInstance;

  // 新增：当前会话的 conversationId，供连续对话复用
  private _conversationId: string | null = null;

  constructor() {
    const baseUrl = window.location.origin + '/editor/svg';
    this._instance = axios.create({
      baseURL: baseUrl,
      // timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 初始化 Dify 客户端
    this._difyInstance = axios.create({
      baseURL: 'http://192.168.2.58:30010/v1',
      // timeout: 60000,
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer app-JHhfCnZBr90LU6o4fhS2KNvi',
      },
    });

    this._setupInterceptors();
  }

  private _setupInterceptors(): void {
    // 只处理权限，不改写响应结构
    this._instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (error) => {
        console.error('SVG API 响应错误:', error);
        const loginRoute = (window as any).AGIMX_CONFIG?.lOGIN_ROUTE;
        if (error?.response?.status === 401 && loginRoute) {
          window.location.href = loginRoute;
        }
        return Promise.reject(error);
      }
    );
  }

  private _handleError(error: any, contextMessage: string): Error {
    console.error(contextMessage, error);
    if (error?.response?.data?.message) {
      return new Error(`${contextMessage}: ${error.response.data.message}`);
    }
    if (error?.message) {
      return new Error(`${contextMessage}: ${error.message}`);
    }
    return new Error(contextMessage);
  }

  setUserId(userId: string): void {
    this._userId = userId;
  }

  getUserId(): string {
    return this._userId;
  }

  // 新增：允许外部（如切换项目时）重置当前 Dify 会话
  public resetConversation(): void {
    this._conversationId = null;
  }

  // 如后续需要手动注入历史会话 id，可暴露这个方法
  public setConversationId(conversationId: string | null): void {
    this._conversationId = conversationId;
  }

  public getConversationId(): string | null {
    return this._conversationId;
  }

  // ========= SVG AI 相关 =========

  // 新增：从模型返回的文本中提取 <svg>...</svg> 片段
  private _extractSvgContent(rawText: string): string {
    const text = rawText.trim();

    const svgStart = text.indexOf('<svg');
    const svgEnd = text.lastIndexOf('</svg>');

    if (svgStart === -1 || svgEnd === -1) {
      throw new Error('未在 AI 返回内容中找到 SVG 片段');
    }

    const svgContent = text.substring(svgStart, svgEnd + '</svg>'.length).trim();

    if (!svgContent.startsWith('<svg') || !svgContent.endsWith('</svg>')) {
      throw new Error('提取到的 SVG 片段不合法');
    }

    return svgContent;
  }

  // 新增：调用 Dify ChatFlow 的封装
  private async _callDifyGenerateSvg(
    prompt: string
  ): Promise<string> {
    try {
      const response = await this._difyInstance.post<DifyChatResponse>('/chat-messages', {
        // inputs 可以按需扩展，目前留空即可
        inputs: {},
        query: prompt,
        response_mode: 'blocking',
        user: this._userId,
        // 关键：把上一次会话的 conversationId 传回去，建立连续对话
        conversation_id: this._conversationId,
      });

      const answerText = response.data?.answer ?? '';

      if (!answerText) {
        throw new Error('AI 返回内容为空');
      }

      // 新增：如果本次返回了 conversation_id，则更新本地会话状态
      const nextConversationId = response.data?.conversation_id;
      if (nextConversationId) {
        this._conversationId = nextConversationId;
      }

      // 兼容 qwq-32b 带 <think>...</think> 的输出，只保留 SVG 部分
      const svgText = this._extractSvgContent(answerText);

      return svgText;
    } catch (error) {
      throw this._handleError(error, '调用 Dify 生成 SVG 失败');
    }
  }

  public async generateSvgByPrompt(
    payload: AiGenerateSvgRequest
  ): Promise<AiGenerateSvgResponse> {
    try {
      // 直接通过 Dify 生成 SVG
      const svgContent = await this._callDifyGenerateSvg(payload.prompt);

      // 由于你已有 mock / 旧后端结构，这里组装成兼容的响应结构
      const result: AiGenerateSvgResponse = {
        svg: svgContent,
        // model 字段可以填入 Dify 应用 ID / 描述，方便前端历史记录
        model: 'dify-chatflow-svg',
      };

      return result;
    } catch (error) {
      throw this._handleError(error, 'AI 生成 SVG 失败');
    }
  }

  public async createAiHistory(
    record: Partial<SvgAiHistory>
  ): Promise<SvgAiHistory> {
    try {
      const response = await this._instance.post<SvgAiHistory>(
        '/items/svg_ai_history',
        record
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error, '保存 SVG AI 历史失败');
    }
  }

  public async getAiHistoryList(): Promise<SvgAiHistory[]> {
    try {
      const response = await this._instance.get<SvgAiHistory[]>(
        '/items/svg_ai_history'
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error, '获取 SVG AI 历史失败');
    }
  }

  // ========= 项目 / 画布 =========

  public async getProjects(): Promise<SvgProject[]> {
    try {
      const response = await this._instance.get<SvgProject[]>(
        '/items/svg_projects'
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error, '获取 SVG 项目列表失败');
    }
  }

  public async getCanvasById(id: string): Promise<SvgCanvasRecord> {
    try {
      const response = await this._instance.get<SvgCanvasRecord>(
        `/items/svg_canvas/${id}`
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error, '获取 SVG 画布失败');
    }
  }

  public async createCanvas(
    record: Partial<SvgCanvasRecord>
  ): Promise<SvgCanvasRecord> {
    try {
      const response = await this._instance.post<SvgCanvasRecord>(
        '/items/svg_canvas',
        record
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error, '创建 SVG 画布失败');
    }
  }

  public async updateCanvas(
    id: string,
    record: Partial<SvgCanvasRecord>
  ): Promise<SvgCanvasRecord> {
    try {
      const response = await this._instance.patch<SvgCanvasRecord>(
        `/items/svg_canvas/${id}`,
        record
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error, '更新 SVG 画布失败');
    }
  }

  public async saveCanvasSnapshot(
    canvasId: string,
    snapshot: SvgCanvasData,
    diff?: Record<string, unknown>
  ): Promise<SvgCanvasHistory> {
    try {
      const response = await this._instance.post<SvgCanvasHistory>(
        '/items/svg_canvas_history',
        {
          canvas_id: canvasId,
          full_snapshot: snapshot,
          diff,
          operator: this._userId,
        }
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error, '保存画布历史失败');
    }
  }

  public async getCanvasHistory(
    canvasId: string
  ): Promise<SvgCanvasHistory[]> {
    try {
      const response = await this._instance.get<SvgCanvasHistory[]>(
        `/items/svg_canvas_history?filter[canvas_id][_eq]=${canvasId}`
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error, '获取画布历史失败');
    }
  }

  // ========= 素材库 =========

  public async getAssets(): Promise<SvgAsset[]> {
    try {
      const response = await this._instance.get<SvgAsset[]>(
        '/items/svg_assets'
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error, '获取 SVG 素材库失败');
    }
  }

  public async createAsset(
    record: Partial<SvgAsset>
  ): Promise<SvgAsset> {
    try {
      const response = await this._instance.post<SvgAsset>(
        '/items/svg_assets',
        record
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error, '创建 SVG 素材失败');
    }
  }

  public async deleteAsset(id: string): Promise<void> {
    try {
      await this._instance.delete<void>(`/items/svg_assets/${id}`);
    } catch (error) {
      throw this._handleError(error, '删除 SVG 素材失败');
    }
  }

  // 新增：导出功能（浏览器端实现）
  public downloadCanvasAsJson(canvas: SvgCanvasData) {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(canvas, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", `canvas_${canvas.canvasId}.json`);
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  }

  public downloadCanvasAsSvg(svgElementInfo: { width: number; height: number; innerHtml: string }, fileName: string = 'export.svg') {
    // 构造完整的 SVG 字符串
    const svgString = `
<svg width="${svgElementInfo.width}" height="${svgElementInfo.height}" viewBox="0 0 ${svgElementInfo.width} ${svgElementInfo.height}" xmlns="http://www.w3.org/2000/svg">
  ${svgElementInfo.innerHtml}
</svg>`;
    
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // ========= 通用 HTTP =========

  public async get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this._instance.get<T>(url, config);
      return response.data;
    } catch (error) {
      throw this._handleError(error, `GET ${url} 失败`);
    }
  }

  public async post<T>(url: string, data?: any): Promise<T> {
    try {
      const response = await this._instance.post<T>(url, data);
      return response.data;
    } catch (error) {
      throw this._handleError(error, `POST ${url} 失败`);
    }
  }
}

const svgAPIService = new SvgAPIService();

export default svgAPIService;